import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Select } from '../../components/ui/select';
import { Notice } from '../../components/ui/notice';
import { useI18n } from '../../i18n/i18n';
import type { MessageKey } from '../../i18n/en';
import type { AuditAction, AuditEntry } from '../../workspaces/workspaces-api';
import { useCurrentWorkspace } from '../../workspaces/current-workspace';

/** «Любой» в фильтрах: Select не принимает пустую строку значением. */
const ANY = '__any';

const ACTIONS: readonly AuditAction[] = [
  'workspace.created',
  'workspace.renamed',
  'workspace.archived',
  'workspace.restored',
  'member.role-changed',
  'member.removed',
  'member.left',
  'invitation.created',
  'invitation.resent',
  'invitation.revoked',
  'invitation.accepted',
  'invitation.declined',
  'diagram.created',
  'diagram.updated',
  'diagram.deleted',
  'diagram.edited',
  'diagram.undone',
  'diagram.redone',
  'diagram.restored',
  'diagram.version-created',
  'diagram.comment-deleted',
];

const selectClass =
  'h-9 rounded-sm border border-border bg-input px-2 text-sm focus:shadow-[var(--shadow-focus-ring)] focus:outline-none';

interface Filter {
  actorUserId: string;
  action: AuditAction | '';
  from: string;
  to: string;
}

/** Журнал действий — владельцу: кто, что, когда; фильтры по автору, действию и периоду. */
export function AuditPage() {
  const { t, locale } = useI18n();
  const workspace = useCurrentWorkspace();
  const [filter, setFilter] = useState<Filter>({ actorUserId: '', action: '', from: '', to: '' });
  const members = useQuery({
    queryKey: ['workspaces', workspace.id, 'members', 'all-for-filter'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/workspaces/{workspaceId}/members', {
          params: { path: { workspaceId: workspace.id }, query: { limit: 100 } },
        }),
      ),
  });
  const query = {
    ...(filter.actorUserId === '' ? {} : { actorUserId: filter.actorUserId }),
    ...(filter.action === '' ? {} : { action: filter.action }),
    // Даты — границы суток в часовом поясе пользователя; конец периода не включительно.
    ...(filter.from === '' ? {} : { from: new Date(`${filter.from}T00:00:00`).toISOString() }),
    ...(filter.to === '' ? {} : { to: nextDay(filter.to).toISOString() }),
  };
  const entries = useInfiniteQuery({
    queryKey: ['workspaces', workspace.id, 'audit', query],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/v1/workspaces/{workspaceId}/audit', {
          params: {
            path: { workspaceId: workspace.id },
            query: pageParam === undefined ? query : { ...query, cursor: pageParam },
          },
        }),
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const items = entries.data?.pages.flatMap((page) => page.items) ?? [];
  const update = (patch: Partial<Filter>) => setFilter((current) => ({ ...current, ...patch }));

  return (
    <Card title={t('workspace.tab.audit')}>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <div className="flex flex-col gap-1.5 font-medium">
          {t('audit.filter.actor')}
          <Select<string>
            aria-label={t('audit.filter.actor')}
            value={filter.actorUserId === '' ? ANY : filter.actorUserId}
            onValueChange={(value) => update({ actorUserId: value === ANY ? '' : value })}
            options={[
              { value: ANY, label: t('audit.filter.any') },
              ...(members.data?.items ?? []).map((member) => ({
                value: member.userId,
                label: member.name ?? member.userId,
              })),
            ]}
          />
        </div>
        <div className="flex flex-col gap-1.5 font-medium">
          {t('audit.filter.action')}
          <Select<string>
            aria-label={t('audit.filter.action')}
            value={filter.action === '' ? ANY : filter.action}
            onValueChange={(value) =>
              update({ action: value === ANY ? '' : (value as AuditAction) })
            }
            options={[
              { value: ANY, label: t('audit.filter.any') },
              ...ACTIONS.map((action) => ({ value: action, label: t(`audit.action.${action}`) })),
            ]}
            className="min-w-56"
          />
        </div>
        <label className="flex flex-col gap-1.5 font-medium">
          {t('audit.filter.from')}
          <input
            type="date"
            className={selectClass}
            value={filter.from}
            onChange={(event) => update({ from: event.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1.5 font-medium">
          {t('audit.filter.to')}
          <input
            type="date"
            className={selectClass}
            value={filter.to}
            onChange={(event) => update({ to: event.target.value })}
          />
        </label>
      </div>
      {entries.isError && <Notice tone="danger">{t(errorMessageKey(entries.error))}</Notice>}
      {items.length === 0 && entries.isSuccess && (
        <p className="text-sm text-muted-foreground">{t('audit.empty')}</p>
      )}
      <ul className="divide-y divide-border text-sm">
        {items.map((entry) => (
          <li key={entry.id} className="flex items-start justify-between gap-4 py-2">
            <div className="min-w-0">
              <p>
                <span className="font-medium">{entry.actor.name ?? t('common.formerUser')}</span>{' '}
                {t(`audit.action.${entry.action}` as MessageKey)}
              </p>
              <p className="text-2xs text-muted-foreground">{describe(entry)}</p>
            </div>
            <span className="whitespace-nowrap text-2xs text-muted-foreground">
              {new Date(entry.createdAt).toLocaleString(locale)}
            </span>
          </li>
        ))}
      </ul>
      {entries.hasNextPage && (
        <Button type="button" variant="secondary" onClick={() => void entries.fetchNextPage()}>
          {t('common.showMore')}
        </Button>
      )}
    </Card>
  );
}

function nextDay(date: string): Date {
  const day = new Date(`${date}T00:00:00`);
  day.setDate(day.getDate() + 1);
  return day;
}

/** Подробности записи журнала — значения из API как есть (простые значения, AGENTS.md §9). */
function describe(entry: AuditEntry): string {
  const details = Object.entries(entry.meta)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(', ');
  return [details, entry.ip].filter((part) => part !== null && part !== '').join(' · ');
}
